/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { concat, of, from, defer } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import {
    mergeMap,
    filter,
    catchError,
    toArray,
    mergeAll,
    map,
    withLatestFrom,
    pluck,
} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import isNil from 'lodash/isNil';
import moment from 'moment';

import { SecurityHelper, STATUS } from 'sarsaparilla';
import { getFacilityAvailabilityUrl } from '../api/endpoints';

import {
    fetchFacilityAvailabilitySuccess,
    fetchFacilityAvailabilityError,
    setFetchFacilityAvailabilityStatus,
    FETCH_FACILITY_AVAILABILITY,
    MONTHS_TO_FETCH,
} from '../actions/facilityAvailabilityActions';

import { setDateFilter, filterTourList } from '../actions/filtersActions';

import { COMMERCIAL_ACCOUNT_TYPE, MAX_STALE_TIME } from '../constants';
import { filterTourPrice } from '../utils/guestTypesUtils';
import { includeFirstComeFirstServed as includeFirstComeFirstServedUtil } from '../utils/generalTicketUtils';

/**
 * Helper function to format the API response
 */
function transformResponses({
    responses,
    isAvailableState,
    isNotReleasedState,
    availableToursState,
    notReleasedToursState,
    firstComeFirstServedToursState,
    lastFetched,
    accountType,
    isSoldOutState,
    isFirstComeFirstServedState,
    nextReleaseState,
    includeFirstComeFirstServed,
    isScheduledState,
}) {
    const isAvailableByDate = { ...isAvailableState };
    const isNotReleasedByDate = { ...isNotReleasedState };
    const availableTourIdsByDate = { ...availableToursState };
    const notReleasedTourIdsByDate = { ...notReleasedToursState };
    const firstComeFirstServedToursByDate = { ...firstComeFirstServedToursState };
    const nextReleaseByDate = { ...nextReleaseState };
    const isSoldOutByDate = { ...isSoldOutState };
    const isFirstComeFirstServedByDate = { ...isFirstComeFirstServedState };
    const isScheduledByDate = { ...isScheduledState };
    const now = moment();

    if (!Array.isArray(responses)) {
        return null;
    }

    responses.forEach((data) => {
        const dayKeys = Object.keys(data);

        dayKeys.forEach((day) => {
            const dayData = data[day];
            const dataByTourId = dayData?.tour_availability_summary_view_by_tour_id || {};
            const tourIdKeys = Object.keys(dataByTourId);
            const monthKey = day.slice(0, -3);
            const soldOutByDate = {};
            const firstComeFirstServedByDate = {};

            tourIdKeys.forEach((tourId) => {
                const tourPrice = {
                    tour_id: tourId,
                };
                const isCommercialAccount = accountType === COMMERCIAL_ACCOUNT_TYPE;
                if (filterTourPrice(tourPrice, isCommercialAccount)) {
                    const {
                        has_reservable: hasReservable,
                        has_not_yet_released: hasNotYetReleased,
                        next_release_timestamp: nextReleaseTime,
                        reserved_count: reservedCount,
                        scheduled_count: scheduledCount,
                        has_walk_up: hasWalkUp,
                        walk_up: walkUp,
                    } = dataByTourId[tourId];

                    // Update available tour IDs
                    if (hasReservable) {
                        if (isNil(availableTourIdsByDate[monthKey])) {
                            availableTourIdsByDate[monthKey] = {};
                        }
                        if (isNil(availableTourIdsByDate[monthKey][day])) {
                            availableTourIdsByDate[monthKey][day] = [tourId];
                        } else {
                            availableTourIdsByDate[monthKey][day].push(tourId);
                        }
                    }

                    // Update availability counts
                    if (isNil(isAvailableByDate[monthKey])) {
                        isAvailableByDate[monthKey] = {};
                    }
                    if (hasReservable) {
                        isAvailableByDate[monthKey][day] = hasReservable;
                    }

                    // update scheduled counts
                    if (isNil(isScheduledByDate[monthKey])) {
                        isScheduledByDate[monthKey] = {};
                    }
                    if (scheduledCount) {
                        isScheduledByDate[monthKey][day] = true;
                    }

                    // Update unreleased tour IDs
                    if (hasNotYetReleased) {
                        if (isNil(notReleasedTourIdsByDate[monthKey])) {
                            notReleasedTourIdsByDate[monthKey] = {};
                        }
                        if (isNil(notReleasedTourIdsByDate[monthKey][day])) {
                            notReleasedTourIdsByDate[monthKey][day] = [tourId];
                        } else {
                            notReleasedTourIdsByDate[monthKey][day].push(tourId);
                        }
                    }

                    // Update unreleased counts
                    if (isNil(isNotReleasedByDate[monthKey])) {
                        isNotReleasedByDate[monthKey] = {};
                    }
                    if (hasNotYetReleased) {
                        isNotReleasedByDate[monthKey][day] = hasNotYetReleased;
                    }

                    if (!soldOutByDate?.[monthKey]) {
                        soldOutByDate[monthKey] = {};
                    }

                    if (!soldOutByDate?.[monthKey]?.[day]) {
                        soldOutByDate[monthKey][day] = [];
                    }

                    const isSoldOut =
                        !!reservedCount &&
                        !!scheduledCount &&
                        reservedCount >= scheduledCount;
                    if (scheduledCount) {
                        soldOutByDate[monthKey][day].push(isSoldOut);
                    }

                    if (!firstComeFirstServedByDate?.[monthKey]) {
                        firstComeFirstServedByDate[monthKey] = {};
                    }

                    if (!firstComeFirstServedByDate?.[monthKey]?.[day]) {
                        firstComeFirstServedByDate[monthKey][day] = [];
                    }

                    if (includeFirstComeFirstServed) {
                        firstComeFirstServedByDate?.[monthKey]?.[day].push(hasWalkUp);

                        // Update first come first served tour IDs
                        if (hasWalkUp) {
                            if (isNil(firstComeFirstServedToursByDate[monthKey])) {
                                firstComeFirstServedToursByDate[monthKey] = {};
                            }
                            if (isNil(firstComeFirstServedToursByDate[monthKey][day])) {
                                firstComeFirstServedToursByDate[monthKey][day] = {};
                            }

                            firstComeFirstServedToursByDate[monthKey][day][tourId] =
                                walkUp;
                        }
                    }

                    // Update next release time
                    if (isNil(nextReleaseByDate[monthKey])) {
                        nextReleaseByDate[monthKey] = {};
                    }
                    if (nextReleaseTime) {
                        nextReleaseByDate[monthKey][day] = nextReleaseTime;
                    }

                    if (
                        nextReleaseTime &&
                        now.isAfter(moment(nextReleaseTime)) &&
                        hasNotYetReleased
                    ) {
                        isAvailableByDate[monthKey][day] = hasNotYetReleased;
                    }
                }
            });

            // Update sold out dates
            if (isNil(isSoldOutByDate[monthKey])) {
                isSoldOutByDate[monthKey] = {};
            }

            isSoldOutByDate[monthKey][day] =
                !!soldOutByDate?.[monthKey]?.[day]?.length &&
                !soldOutByDate?.[monthKey]?.[day]?.some((item) => item === false);

            // Update first come first served dates
            if (isNil(isFirstComeFirstServedByDate[monthKey])) {
                isFirstComeFirstServedByDate[monthKey] = {};
            }

            isFirstComeFirstServedByDate[monthKey][day] =
                !!firstComeFirstServedByDate?.[monthKey]?.[day]?.length &&
                firstComeFirstServedByDate?.[monthKey]?.[day]?.some(
                    (item) => item === true
                );
        });
    });

    return {
        isAvailableByDate,
        isNotReleasedByDate,
        isSoldOutByDate,
        isFirstComeFirstServedByDate,
        availableTourIdsByDate,
        notReleasedTourIdsByDate,
        firstComeFirstServedToursByDate,
        lastFetched,
        lastFetchedAccountType: accountType,
        nextReleaseByDate,
        isScheduledByDate,
    };
}

/**
 * Fetches availability for all tours in a facility for x number of months
 *   - where x = MONTHS_TO_FETCH
 *
 * Combines those availabilities to represent generalized availability
 * for the entire facility.
 *
 * We store the fetch time as well, and when the user navigates the calendar,
 * we only pull fresh data if the existing data is stale (over 1 min old)
 */
export const fetchAvailabilityByFacility = (actions$, state$) =>
    actions$.pipe(
        ofType(FETCH_FACILITY_AVAILABILITY),
        filter(({ payload }) => !!payload?.facilityId && !!payload?.date),
        withLatestFrom(state$.pipe(pluck('timedEntry'))),
        mergeMap(([{ payload }, timedEntryState]) => {
            const availability = timedEntryState.facilityAvailability;
            const accountType = timedEntryState.accountType.accountType;

            const { facilityId, date, numberOfMonths = MONTHS_TO_FETCH } = payload;

            const includeFirstComeFirstServed =
                includeFirstComeFirstServedUtil(facilityId);
            const startDate = date.clone().startOf('month');
            const lastFetchedState = availability?.lastFetched || {};
            const lastFetchedAccountType = availability?.lastFetchedAccountType;
            const isAccountTypeChanged =
                Boolean(lastFetchedAccountType) && accountType !== lastFetchedAccountType;
            const isAvailableState = availability?.isAvailableByDate || {};
            const isNotReleasedState = availability?.isNotReleasedByDate || {};
            const isSoldOutState = availability?.isSoldOutByDate || {};
            const isFirstComeFirstServedState =
                availability?.isFirstComeFirstServedByDate || {};
            const availableToursState = availability?.availableTourIdsByDate || {};
            const notReleasedToursState = availability?.notReleasedTourIdsByDate || {};
            const firstComeFirstServedToursState =
                availability?.firstComeFirstServedToursByDate || {};
            const nextReleaseState = availability?.nextReleaseByDate || {};
            const isScheduledState = availability?.isScheduledByDate || {};

            const lastFetched = { ...lastFetchedState };
            const now = new Date().getTime();

            /**
             * Delete stale data from our cached states
             * For now, not deleting calendar-related things because loading is slow and it
             * causes the calendar to flash availability in a not nice way
             */
            const lastFetchedKeys = Object.keys(lastFetchedState);
            lastFetchedKeys.forEach((monthKey) => {
                const lastFetchedTime = lastFetchedState[monthKey];
                const isStale = now - lastFetchedTime > MAX_STALE_TIME;

                if (isStale) {
                    // delete isAvailableState[monthKey];
                    // delete isNotReleasedState[monthKey];
                    delete availableToursState[monthKey];
                    delete notReleasedToursState[monthKey];
                    delete firstComeFirstServedToursState[monthKey];
                    delete isSoldOutState[monthKey];
                    delete isFirstComeFirstServedState[monthKey];
                }
                if (isAccountTypeChanged) {
                    delete isAvailableState[monthKey];
                    delete isNotReleasedState[monthKey];
                    delete availableToursState[monthKey];
                    delete notReleasedToursState[monthKey];
                    delete firstComeFirstServedToursState[monthKey];
                    delete isSoldOutState[monthKey];
                    delete isFirstComeFirstServedState[monthKey];
                }
            });

            /**
             * Create array of dates to fetch. We need to fetch the requested month
             */
            const monthsToFetch = [];

            // Forward months
            for (let i = 0; i < numberOfMonths; i += 1) {
                const month = startDate.clone().add(i, 'months');
                const monthKey = month.format('YYYY-MM');

                if (
                    !lastFetchedState[monthKey] ||
                    now - lastFetchedState[monthKey] > MAX_STALE_TIME
                ) {
                    monthsToFetch.push(month);
                }
            }

            // Update lastFetched
            monthsToFetch.forEach((month) => {
                const formattedMonth = month.format('YYYY-MM');
                lastFetched[formattedMonth] = now;
            });

            // Create array of deferred requests
            const requests = monthsToFetch.map((item) => {
                const month = item.format('MM');
                const year = item.format('YYYY');
                const url = getFacilityAvailabilityUrl({
                    facilityId,
                    month,
                    year,
                    accountType,
                });

                return defer(() =>
                    ajax({
                        url,
                        method: 'GET',
                        headers: {
                            Authorization: SecurityHelper.getAuthHeader(),
                        },
                    })
                        .pipe(pluck('response'))
                        .pipe(
                            pluck('facility_availability_summary_view_by_local_date'),
                            catchError((error) => {
                                // if this error, let it process as empty dates for the month rather
                                // than throwing an error to the user
                                if (error?.response?.error === 'invalid year/month') {
                                    return of({});
                                }
                                throw error;
                            })
                        )
                );
            });

            // ajax call stream, calls 5 at a time
            const ajax$ = from(requests).pipe(mergeAll(5), toArray());

            const maybeResetDate = isAccountTypeChanged
                ? setDateFilter(null)
                : { type: 'null' };

            return concat(
                of(setFetchFacilityAvailabilityStatus(STATUS.PENDING)),
                of(maybeResetDate),
                ajax$.pipe(
                    map((responses) => {
                        const data = transformResponses({
                            responses,
                            isAvailableState,
                            isNotReleasedState,
                            availableToursState,
                            notReleasedToursState,
                            firstComeFirstServedToursState,
                            lastFetched,
                            accountType,
                            isSoldOutState,
                            isFirstComeFirstServedState,
                            nextReleaseState,
                            includeFirstComeFirstServed,
                            isScheduledState,
                        });

                        return fetchFacilityAvailabilitySuccess(data);
                    }),
                    catchError((error) => of(fetchFacilityAvailabilityError(error)))
                ),
                of(filterTourList())
            );
        })
    );
